import { type HistoryLocation, type RouterHistory, createHistory } from "@tanstack/history";

export interface HostHistory extends RouterHistory {
    /** Host-fed route ("path?query#hash", no leading slash); never reported back via `onNavigate`. */
    setRoute(route: string): void;
}

/**
 * In-memory history for a view whose location belongs to an embedding host:
 * the host sets it, navigation inside the view is reported out and applied
 * locally right away (the host may echo it back; equal routes are no-ops).
 */
export function createHostHistory(
    initialRoute: string,
    onNavigate: (route: string, replace: boolean) => void
): HostHistory {
    let entries = [toHref(initialRoute)];
    let index = 0;
    let key = 0;

    const getLocation = (): HistoryLocation => {
        const url = new URL(entries[index], "http://host");
        return {
            href: url.pathname + url.search + url.hash,
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
            state: { __TSR_index: index, __TSR_key: String(key) },
        };
    };

    const history = createHistory({
        getLocation,
        getLength: () => entries.length,
        pushState: (href) => {
            entries = [...entries.slice(0, index + 1), href];
            index++;
            key++;
            onNavigate(toRoute(href), false);
        },
        replaceState: (href) => {
            entries[index] = href;
            key++;
            onNavigate(toRoute(href), true);
        },
        go: (n) => {
            index = Math.min(Math.max(index + n, 0), entries.length - 1);
            onNavigate(toRoute(entries[index]), false);
        },
        back: () => {
            index = Math.max(index - 1, 0);
            onNavigate(toRoute(entries[index]), false);
        },
        forward: () => {
            index = Math.min(index + 1, entries.length - 1);
            onNavigate(toRoute(entries[index]), false);
        },
        createHref: (href) => href,
    });

    return Object.assign(history, {
        setRoute(route: string) {
            const href = toHref(route);
            if (href === entries[index]) {
                return;
            }
            entries[index] = href;
            key++;
            history.notify({ type: "REPLACE" });
        },
    });
}

function toHref(route: string) {
    return "/" + route.replace(/^\//, "");
}

function toRoute(href: string) {
    return href.replace(/^\//, "");
}
